import React, {useCallback, useEffect, useState} from 'react';
import {useIntl} from 'react-intl';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import Drawer from '@material-ui/core/Drawer';
import List from '@material-ui/core/List';
import Tooltip from '@material-ui/core/Tooltip';
import {HiOutlineUpload} from '@react-icons/all-files/hi/HiOutlineUpload';
import {HiOutlineHome} from '@react-icons/all-files/hi/HiOutlineHome';
import {HiOutlineUsers} from '@react-icons/all-files/hi/HiOutlineUsers';
import {HiOutlineLogout} from '@react-icons/all-files/hi/HiOutlineLogout';
import {TbSquareX} from 'react-icons/tb';
import classnames from 'classnames';
import isEmpty from 'lodash.isempty';
import {selectAccessToken} from '@components/Login/userSlice';
import {parseJWT, getIsMemberToken} from '@utils/jwtUtils';
import {AUTH_USER_ROLE} from '@constants/user';
import {deauthorizeExchangeSession} from '@api/authorizationToken/api';
import DocumentsLayers from '@components/shared/icons/DocumentsLayers';
import Document from './Document/Document';
import Attachment from './Attachment/Attachment';
import {
    selectIsAttachmentsMenuOpen,
    selectIsDocumentsMenuOpen,
    selectIsLeftMenuOpen,
    selectSelectedAttachmentId,
    selectTransaction,
    selectAttachments,
    selectRecipient,
    selectMergedTransactionSettings,
    setIsAttachmentsMenuOpen,
    setIsDeclinePanelOpen,
    setIsDocumentsMenuOpen,
    setIsLeftMenuOpen,
    setIsReassignPaneOpen,
    exitFaceToFaceSigning
} from '@components/SignerUi/signerUiSlice';
import {useFaceToFaceRole} from '@components/SignerUi/hooks/useFaceToFaceRole';
import {useDocumentsWithStatuses, getAreAllDocumentsConfirmed} from '@components/SignerUi/hooks/useDocumentStatuses';
import {getAreAllRequiredAttachmentsCompleted} from '@components/SignerUi/utils/getAttachmentStatuses';
import {SCREEN_SIZE, CEREMONY_PATH, CEREMONY_F2F_PATH, TRANSACTION_PATH} from '@constants/index';
import {RECIPIENT_STATUS} from '@constants/signer';
import {RECIPIENT_PLACEHOLDERS} from '@api/constants';
import DownloadAllButton from './Document/DownloadAllButton/DownloadAllButton';
import LanguageSelector from './LanguageSelector/LanguageSelector';
import useWindowSize from '@hooks/useWindowSize';
import {getActionCounts} from '@components/SignerUi/utils/getDocumentStatuses';
import getDocIconAndStatus from '@components/SignerUi/utils/getDocIconAndStatus';
import {useDownloadDocument} from '@hooks/useDownloadDocument';
import './signerUiMenu.less';

function MenuItem({
    icon,
    label,
    onClick,
    isOpen,
    isMenuOpen,
    className,
    dataWdio,
    children
}) {
    const itemClassName = classnames('signing-menu-item', className, {
        'is-open': isOpen,
        'has-submenu': !!children
    });

    return (
        <div className={itemClassName}>
            <button
                type="button"
                className="signing-menu-item-btn"
                onClick={onClick}
                tabIndex={isMenuOpen ? 0 : -1}
                data-wdio={dataWdio}
            >
                <span className="signing-menu-item-icon">{icon}</span>
                {isMenuOpen && <span className="signing-menu-item-label">{label}</span>}
            </button>
            {isOpen && children && (
                <div className="signing-menu-item-content">
                    {children}
                </div>
            )}
        </div>
    );
}

export default function SignerUiMenu() {
    const intl = useIntl();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const isMenuOpen = useSelector(selectIsLeftMenuOpen);
    const isDocumentsMenuOpen = useSelector(selectIsDocumentsMenuOpen);
    const isAttachmentsMenuOpen = useSelector(selectIsAttachmentsMenuOpen);
    const selectedAttachmentId = useSelector(selectSelectedAttachmentId);
    const documents = useDocumentsWithStatuses();
    const transaction = useSelector(selectTransaction) || {};
    const attachments = useSelector(selectAttachments) || [];
    const recipient = useSelector(selectRecipient);
    const allApprovals = useSelector((state) => state.signerUi.approvals) || {};
    const allFields = useSelector((state) => state.signerUi.fields) || {};
    const mergedTransactionSettings = useSelector(selectMergedTransactionSettings);
    const accessToken = useSelector(selectAccessToken);
    const {isFaceToFaceRecipient} = useFaceToFaceRole();

    const tokenPayload = parseJWT(accessToken);
    const isUserRole = tokenPayload?.role === AUTH_USER_ROLE;
    const isMemberToken = !!accessToken && getIsMemberToken(accessToken);
    const shouldShowReturnToTransaction = isUserRole || isMemberToken;

    const {downloadSignedDocument} = useDownloadDocument({
        transactionId: transaction?.id,
        flatten: false
    });

    const windowSize = useWindowSize();
    const isMobile = windowSize.width <= SCREEN_SIZE.SM;

    const isReviewer = recipient?.status === RECIPIENT_STATUS.REVIEWER;
    const isCurrentSigner = recipient?.status === RECIPIENT_STATUS.CURRENT_SIGNER;
    const isOwnerPlaceholder = recipient?.placeholder?.toLowerCase() === RECIPIENT_PLACEHOLDERS.OWNER;
    const isDeclineButtonEnabled = mergedTransactionSettings?.show_decline_button === 'true';
    const isLeftMenuExpandEnabled = mergedTransactionSettings?.left_menu_expand === 'true';
    const isAllDocumentsConfirmed = getAreAllDocumentsConfirmed(documents);
    const isAllRequiredAttachmentsCompleted = getAreAllRequiredAttachmentsCompleted(attachments);
    const isSigningCompleted = isAllDocumentsConfirmed && isAllRequiredAttachmentsCompleted;
    const shouldShowDeclineOption = isCurrentSigner
        && !isOwnerPlaceholder
        && isDeclineButtonEnabled
        && !isSigningCompleted;
    const shouldShowReassign = isCurrentSigner && !!recipient?.change_recipient;
    const isDownloadAllowed = true;
    const hasAttachments = !isEmpty(attachments);
    const shouldShowExitSigning = isFaceToFaceRecipient;

    const [isExitSigningLoading, setIsExitSigningLoading] = useState(false);

    const getDocumentIconAndStatus = getDocIconAndStatus({isReviewer});

    const wrapperClassName = classnames('signing-left-menu-wrapper', {
        'is-open': isMenuOpen
    });

    const menuClassName = classnames('signing-left-menu', {
        'is-open': isMenuOpen
    });

    function openMenu() {
        dispatch(setIsLeftMenuOpen(true));
    }

    function closeMenu() {
        dispatch(setIsLeftMenuOpen(false));
    }

    function toggleDocumentsMenu() {
        const shouldOpen = !isDocumentsMenuOpen;
        dispatch(setIsDocumentsMenuOpen(shouldOpen));
        dispatch(setIsAttachmentsMenuOpen(false));
        if (shouldOpen) {
            openMenu();
        }
    }

    function toggleAttachmentsMenu() {
        const shouldOpen = !isAttachmentsMenuOpen;
        dispatch(setIsAttachmentsMenuOpen(shouldOpen));
        dispatch(setIsDocumentsMenuOpen(false));
        if (shouldOpen) {
            openMenu();
        }
    }

    async function backToTransaction() {
        const transactionPath = `${TRANSACTION_PATH}/${transaction?.id || ''}`;
        if (isMemberToken) {
            await deauthorizeExchangeSession();
            window.location.href = transactionPath;
            return;
        }
        navigate(transactionPath);
    }

    function openDeclinePanel() {
        dispatch(setIsDeclinePanelOpen(true));
        closeMenu();
    }

    function openReassignPanel() {
        dispatch(setIsReassignPaneOpen(true));
        closeMenu();
    }

    async function handleExitSigning() {
        setIsExitSigningLoading(true);
        await dispatch(exitFaceToFaceSigning());
        closeMenu();
        navigate(`${TRANSACTION_PATH}/${transaction.id}${CEREMONY_PATH}/${CEREMONY_F2F_PATH}`);
        setIsExitSigningLoading(false);
    }

    function getTooltipTitle(key) {
        if (isMenuOpen) {
            return '';
        }
        return intl.formatMessage({id: key, defaultMessage: ''});
    }

    const handleDownload = useCallback(
        (documentId, documentName) => async () => {
            if (!documentId) return;
            try {
                await downloadSignedDocument(documentId, `${documentName}.pdf`);
            } catch (error) {
                console.error('[SignerUiMenu] Failed to download document:', error);
            }
        },
        [downloadSignedDocument]
    );

    useEffect(() => {
        if (windowSize.isDefined) {
            const shouldOpenMenu = !isMobile && isLeftMenuExpandEnabled;
            if (shouldOpenMenu) {
                openMenu();
            } else {
                closeMenu();
            }
        }
    }, [isMobile, windowSize.isDefined, isLeftMenuExpandEnabled]);

    function renderDocumentsList() {
        return (
            <div className="signing-menu-submenu">
                <List classes={{root: 'signing-menu-list'}}>
                    {documents.map((document) => {
                        const documentApprovals = allApprovals[document.id] || [];
                        const documentFields = documentApprovals.flatMap(
                            (approval) => allFields[approval.id] || []
                        );
                        const documentStatus = getActionCounts({
                            approvals: documentApprovals,
                            fields: documentFields
                        });
                        const remainingActions = documentStatus.requiredActionsTotalCount
                            - documentStatus.requiredCompletedActionsCount;
                        const {icon, status} = getDocumentIconAndStatus(document);

                        return (
                            <Document
                                key={document.id}
                                document={document}
                                icon={icon}
                                status={status}
                                remainingActions={remainingActions}
                                onDownload={handleDownload(document.id, document.name)}
                                isDownloadAllowed={isDownloadAllowed}
                            />
                        );
                    })}
                </List>
                {isDownloadAllowed && (
                    <DownloadAllButton
                        zipUrl={transaction?.zipUrl}
                        isSigningComplete={transaction?.isSigningComplete}
                        documents={documents}
                    />
                )}
            </div>
        );
    }

    function renderAttachmentsList() {
        return (
            <div className="signing-menu-submenu">
                <List classes={{root: 'signing-menu-list'}}>
                    {attachments.map((attachment) => (
                        <Attachment
                            key={attachment.id}
                            isSelected={selectedAttachmentId === attachment.id}
                            attachment={attachment}
                            onClick={closeMenu}
                        />
                    ))}
                </List>
            </div>
        );
    }

    return (
        <div className={wrapperClassName}>
            <Drawer
                anchor="left"
                open={isMenuOpen}
                classes={{root: menuClassName}}
                onClose={closeMenu}
                variant="permanent"
            >
                <div className="signing-menu-container">
                    {shouldShowReturnToTransaction && (
                        <Tooltip title={getTooltipTitle('page.designer.nav.backToTransaction')} placement="right" arrow>
                            <div>
                                <MenuItem
                                    icon={<HiOutlineHome className="icon" aria-hidden />}
                                    label={intl.formatMessage({id: 'page.designer.nav.backToTransaction', defaultMessage: ''})}
                                    onClick={backToTransaction}
                                    isMenuOpen={isMenuOpen}
                                />
                            </div>
                        </Tooltip>
                    )}

                    <LanguageSelector shouldShowIcon isMenuOpen={isMenuOpen} />

                    {shouldShowReassign && (
                        <Tooltip title={getTooltipTitle('oss.components.MoreActions.reAssignRecipient')} placement="right" arrow>
                            <div>
                                <MenuItem
                                    icon={<HiOutlineUsers className="icon" aria-hidden />}
                                    label={intl.formatMessage({id: 'oss.components.MoreActions.reAssignRecipient', defaultMessage: ''})}
                                    onClick={openReassignPanel}
                                    isMenuOpen={isMenuOpen}
                                    dataWdio="test-menu-btn-reassign"
                                />
                            </div>
                        </Tooltip>
                    )}

                    <Tooltip title={getTooltipTitle('oss.components.SideBar.documents')} placement="right" arrow>
                        <div>
                            <MenuItem
                                icon={<DocumentsLayers className="icon documents-icon" aria-hidden />}
                                label={`${intl.formatMessage({id: 'oss.components.SideBar.documents', defaultMessage: ''})} (${documents.length})`}
                                onClick={toggleDocumentsMenu}
                                isOpen={isDocumentsMenuOpen}
                                isMenuOpen={isMenuOpen}
                                dataWdio="test-menu-btn-documents"
                            >
                                {renderDocumentsList()}
                            </MenuItem>
                        </div>
                    </Tooltip>

                    {hasAttachments && (
                        <Tooltip title={getTooltipTitle('oss.components.SideBar.Document.uploads')} placement="right" arrow>
                            <div>
                                <MenuItem
                                    icon={<HiOutlineUpload className="icon" aria-hidden />}
                                    label={`${intl.formatMessage({id: 'oss.components.SideBar.Document.uploads', defaultMessage: ''})} (${attachments.length})`}
                                    onClick={toggleAttachmentsMenu}
                                    isOpen={isAttachmentsMenuOpen}
                                    isMenuOpen={isMenuOpen}
                                    dataWdio="test-menu-btn-upload-attachments"
                                >
                                    {renderAttachmentsList()}
                                </MenuItem>
                            </div>
                        </Tooltip>
                    )}

                    {shouldShowExitSigning && (
                        <Tooltip title={getTooltipTitle('oss.components.MoreActions.exitSigning')} placement="right" arrow>
                            <div>
                                <MenuItem
                                    icon={<HiOutlineLogout className="icon" aria-hidden />}
                                    label={intl.formatMessage({id: 'oss.components.MoreActions.exitSigning', defaultMessage: ''})}
                                    onClick={handleExitSigning}
                                    isMenuOpen={isMenuOpen}
                                    className={isExitSigningLoading ? 'is-loading' : ''}
                                    dataWdio="test-menu-btn-exit-signing"
                                />
                            </div>
                        </Tooltip>
                    )}

                    {shouldShowDeclineOption && (
                        <Tooltip title={getTooltipTitle('oss.components.MoreActions.declineTosign')} placement="right" arrow>
                            <div>
                                <MenuItem
                                    icon={<TbSquareX className="icon" aria-hidden />}
                                    label={intl.formatMessage({id: 'oss.components.MoreActions.declineTosign', defaultMessage: ''})}
                                    onClick={openDeclinePanel}
                                    isMenuOpen={isMenuOpen}
                                    className="decline"
                                    dataWdio="test-menu-btn-decline-signing"
                                />
                            </div>
                        </Tooltip>
                    )}
                </div>
            </Drawer>
        </div>
    );
}
